import { Device, User } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { endOfDay, startOfDay, sub } from "date-fns";
import { useEffect, useState } from "react";
import { LogbookExcelExportButton } from "@/components/grid/LogbookExcelExportButton";
import { FilterStackLayout } from "@/components/layout/FilterStackLayout";
import SettingsPopup from "@/components/logbook/SettingsPopup";
import LocalStorageItems from "@/constants/LocalStorageNames";
import { useFilteredTrips } from "@/hooks/logbook/useFilteredTrips";
import { useTripAddresses } from "@/hooks/logbook/useTripAddresses";
import { mapLazy } from "@/utilities/mapLazy";

const TripsGrid = mapLazy("TripsGrid", import("./components/TripsGrid"));
const ReasonInput = mapLazy("ReasonInput", import("@/components/logbook/ReasonInput"));
const DateRangeSelect = mapLazy("DateRangeSelect", import("@/components/input/DateRangeSelect"));
const DriverOrDeviceSelect = mapLazy(
  "DriverOrDeviceSelect",
  import("@/components/input/DriverOrDeviceSelect"),
);

const DEFAULT_LOOKBACK_DAYS = 1;

/**
 * The logbook, for reading and handing on.
 *
 * The same trips the logbook edit page lists, without the editing: pick a period, a vehicle and a
 * reason, and take the result away as a spreadsheet. The start and stop addresses are resolved for
 * every row before the export runs, because a logbook that has to be filed is no use with the
 * addresses of only the rows someone happened to scroll past.
 */
export default function LogbookCheckPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  // Its own keys. The logbook edit page stores its period under the fault overview's keys, so a
  // third page joining that would make all three fight over one period.
  const [fromDate, setFromDate] = useState(() =>
    readDate(
      LocalStorageItems.logbookCheckFromDate,
      startOfDay(sub(new Date(), { days: DEFAULT_LOOKBACK_DAYS })),
    ),
  );
  const [toDate, setToDate] = useState(() =>
    readDate(LocalStorageItems.logbookCheckToDate, endOfDay(new Date())),
  );

  useEffect(() => {
    localStorage.setItem(LocalStorageItems.logbookCheckFromDate, fromDate.toISOString());
    localStorage.setItem(LocalStorageItems.logbookCheckToDate, toDate.toISOString());
  }, [fromDate, toDate]);

  const gridApiRef = useGridApiRef();

  const { data: trips } = useFilteredTrips({ users, devices, fromDate, toDate, reasons });

  // Owned here rather than in the grid, because the export button has to wait for them: a filed
  // logbook with two empty address columns and no hint that anything is missing is worse than a
  // button that stays grey for a moment.
  const addresses = useTripAddresses(trips ?? []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  return (
    <FilterStackLayout
      filterMaxWidth={1512}
      isMobile={isMobile}
      filters={
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <SettingsPopup
              vehicleStatus={showArchived}
              setVehicleStatus={setShowArchived}
              showDriverOptions={false}
            />
            <DriverOrDeviceSelect
              users={users}
              devices={devices}
              // The driver selection is taken too, rather than offering a control that discards
              // it: the picker on this branch cannot be told to hide drivers, and the trip query
              // filters by them just as well.
              onChange={(nextUsers?: User[], nextDevices?: Device[]) => {
                setUsers(nextUsers ?? []);
                setDevices(nextDevices ?? []);
              }}
              onlyDrivers={false}
              showArchived={showArchived}
              showArchivedVehicles={showArchived}
            />
          </Box>
          <ReasonInput
            onChange={setReasons}
          />
          <DateRangeSelect
            fromDate={fromDate}
            toDate={toDate}
            onChange={(nextFrom: Date, nextTo: Date) => {
              setFromDate(nextFrom ?? new Date());
              setToDate(nextTo ?? new Date());
            }}
          />
          <LogbookExcelExportButton
            apiRef={gridApiRef}
            disabled={!trips || trips.length === 0 || !addresses.isReady}
            allColumns
          />
        </Box>
      }
      content={<TripsGrid apiRef={gridApiRef} trips={trips} addresses={addresses} />}
    />
  );
}

function readDate(key: string, fallback: Date): Date {
  const stored = localStorage.getItem(key);
  const parsed = stored ? new Date(stored) : null;
  return parsed && Number.isFinite(parsed.getTime()) ? parsed : fallback;
}
